import React, {useCallback, useEffect, useMemo, useState} from "react";
import Booking from "../../../models/Booking";
import {add} from "date-fns";
import BookingItem from "../../../models/BookingItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import groupBy from "../../../utils/groupBy";
import {useMainLayout} from "../../../layouts/Main";
import useAccommodationUnitSelector from "../../../components/Accommodations/useAccommodationUnitSelector";
import useDialog from "../../../hooks/useDialog";


const generateResourceId = (bookableType, bookableId) => {
    return `${bookableType}-${bookableId}`;
}

const getResourceType = (resourceId) => {
    return resourceId.split('-')[0];
}

const getResourceId = (resourceId) => {
    return Number(resourceId.split('-')[1]);
}


const useBookingsPlanner = ({accommodations, bookings}) => {
    const formBooking = useDialog();

    const {isMobile} = useMainLayout();
    const [viewMode, setViewMode] = React.useState((accommodations.accommodationUnits.length > 1 && !isMobile) ? 'timeline' : 'month-vertical');

    const initialDate = new Date();
    const minDate = new Date(2021, 0, 1); //startOfMonth(sub(initialDate, {months: 6}));
    const maxDate = add(initialDate, {months: 36});

    /*
     * Get the calendar items (enriched booking items) that are between or intersect the given
     * time interval (startDate, endDate).
     *
     * If strictStartDate is true it returns the only calendar items which arrival is also >= startDate.
    */
    const getCalendarItems = useCallback(
        async (startDate, endDate, {bookable=null, strictStartDate=false}) => {
            if(bookings === undefined || bookings === null) {
                return [];
            }

            const predicate = (b) => {
                return (b.departure >= startDate && b.arrival <= endDate && (!strictStartDate || b.arrival >= startDate)) &&
                    (!bookable || (b.bookableType === bookable.type && b.bookableId === bookable.id));
            }

            return bookings.reduce(
                (acc, x) => {
                    const items = x.bookingItems
                        .filter(predicate)
                        .map(b => ({
                            id: b.id,
                            bookingId: x.id,
                            bookableType: b.bookableType,
                            bookableId: b.bookableId,
                            startDate: b.arrival,
                            endDate: b.departure,
                            price: b.price,
                            bookingDeposit: x.bookingFinance.deposit,
                            bookingPayed: x.bookingFinance.totalPayed,
                            bookingPrice: x.bookingFinance.totalPrice,
                            fullName: x.getFullName("Senza nome"),
                            checkOutAt: x.checkOutAt
                        }));
                    return acc.concat(items);
                }, []);
        },
        [bookings]
    );

    const monthViewState = useMonthViewState({getCalendarItems, accommodations});
    const timelineViewState = useTimelineViewState({getCalendarItems, accommodations, openFormBooking: formBooking.open});

    const handleChangeViewMode = (event, newViewMode) => {
        if (newViewMode !== null) {
            setViewMode(newViewMode);
        }
    };

    const handleAfterCreateBooking = (booking) => {
        monthViewState.accommodationUnitSelector.handleChange(
            accommodations.findAccommodationUnit(
                booking.bookingItems[0].bookableType,
                booking.bookingItems[0].bookableId
            )
        )
    }

    const handleCloseFormBooking = () => {
        // reset the new booking in case of a selection in the timeline view
        timelineViewState.resetNewBooking();
        formBooking.close();
    }

    return {
        minDate,
        maxDate,
        initialDate,
        viewMode,
        handleChangeViewMode,
        defaultNewBooking: viewMode === 'timeline' ? timelineViewState.newBooking : monthViewState.newBooking,
        bookingLoader:  viewMode === 'timeline' ? timelineViewState.bookingsLoader : monthViewState.bookingsLoader,
        accommodationUnitSelector: monthViewState.accommodationUnitSelector,
        handleAfterCreateBooking,
        monthVertical: monthViewState,
        timeline: timelineViewState,
        handleSelect: timelineViewState.handleSelect,
        formBooking,
        openFormBooking: formBooking.open,
        closeFormBooking: handleCloseFormBooking,
    }
}

const useMonthViewState = ({getCalendarItems, accommodations}) => {
    const accommodationUnitSelector = useAccommodationUnitSelector(accommodations.accommodationUnits);
    const [newBooking, setNewBooking] = useState(new Booking({bookingItems: []}));

    const selectedAccommodationUnit = accommodationUnitSelector.selectedAccommodationUnit;

    const bookingsLoader = useCallback(
        async (startDate, endDate) => {
            return selectedAccommodationUnit !== null ? getCalendarItems(startDate, endDate, {bookable: selectedAccommodationUnit}) : [];
        },
        [selectedAccommodationUnit, getCalendarItems] // eslint-disable-line react-hooks/exhaustive-deps
    );

    // When the selectedAccommodationUnit changes update the default new booking (The user
    // selected another bookable - for month vertical mode).
    useEffect(
        () => {
            setNewBooking( new Booking({
                bookingItems: selectedAccommodationUnit !== null && selectedAccommodationUnit !== undefined ?
                    [new BookingItem({
                        bookableType: selectedAccommodationUnit.type,
                        bookableId: selectedAccommodationUnit.id,
                    })]
                    :
                    []
            }))
        },
        [selectedAccommodationUnit]
    )

    return {
        bookingsLoader,
        accommodationUnitSelector,
        newBooking,
    }
}

const useTimelineViewState = ({getCalendarItems, accommodations, openFormBooking}) => {
    const [newBooking, setNewBooking] = useState(new Booking({bookingItems: []}));

    const bookingsLoader = useCallback(
        async (startDate, endDate) => {
            const items = await getCalendarItems(startDate, endDate, {strictStartDate:true});
            return groupBy(items, i => generateResourceId(i.bookableType, i.bookableId));
        },
        [accommodations.accommodationUnits, getCalendarItems] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const renderAccommodationLabel = (accommodationUnit) => {
        return <Stack justifyContent="center" sx={{height: '100%'}}>
            <Typography variant="caption" color="text.primary">{accommodationUnit.accommodation.name}</Typography>
            <Typography variant="body2">{accommodationUnit.name}</Typography>
        </Stack>;
    }

    const resources = useMemo(
        () => accommodations.accommodationUnits.map(unit => ({
                resourceId: generateResourceId(unit.type, unit.id),
                renderLabel: () => renderAccommodationLabel(unit),
            })
        ),
        [accommodations.accommodationUnits]
    );

    // When the user select a period in the planner sets the default new booking
    const handleSelect = (from, to, resourceId) => {
        setNewBooking(new Booking({
            bookingItems: [{
                bookableType: getResourceType(resourceId),
                bookableId: getResourceId(resourceId),
                arrival: from,
                departure: to,
            }],
        }));
        openFormBooking();
    }

    const resetNewBooking = () => {
        setNewBooking(new Booking({bookingItems: []}));
    }

    return {
        bookingsLoader,
        resources,
        newBooking,
        handleSelect,
        resetNewBooking
    }
}


export default useBookingsPlanner;